import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { GrupoDePrivilegioService } from '../../services/usuario/grupo-de-privilegio.service';
import { PrivilegioService } from '../../services/usuario/privilegio.service';

import { GrupoDePrivilegioDTO, GrupoDePrivilegioResponseDTO } from '../../models/grupo-de-privilegio.model';
import { PrivilegioDTO } from '../../models/privilegio.model';
import { TableColumn } from '../../models/shared/table-column.model';
import { agruparPrivilegiosPorSecao, SecaoDePrivilegio } from '../../enums/SecaoPrivilegio.enum';

@Component({
  selector: 'app-grupo-de-privilegio',
  templateUrl: './grupo-de-privilegio.component.html',
  styleUrls: ['./grupo-de-privilegio.component.scss'],
  providers: [MessageService],
})
export class GrupoDePrivilegioComponent implements OnInit, OnDestroy {
  form: Partial<GrupoDePrivilegioDTO> = { nome: '', status: true, privilegios: [] };

  // Modo edicao: guarda o ID do grupo sendo editado (null = modo criacao)
  editandoId: number | null = null;

  // Controle de abas (0 = Cadastro, 1 = Lista)
  activeTabIndex: number = 1;

  allSelected = false;
  privilegiosList: PrivilegioDTO[] = [];
  /** privilegiosList agrupada por seção, na ordem em que as telas aparecem no menu. */
  secoesPrivilegio: SecaoDePrivilegio[] = [];
  /** Enviaremos o ENUM da janela, conforme combinado no POST */
  selectedPrivilegios: string[] = [];

  // Dialog de exclusao
  deleteDialogVisible = false;
  deleteItemId: string | null = null;

  // Objetos de traducao
  texts = {
    titulo: '',
    subtitulo: '',
    novo_grupo: '',
    editar_grupo: '',
    tabela_de_grupos: '',
    nenhum_grupo: '',
    ajuste_filtros: '',
    carregando: '',
    sem_privilegios: '',
    confirmacao: '',
    confirmar_exclusao: '',
    aba_cadastro: '',
    aba_lista: ''
  };

  labels = {
    nome_do_grupo: '',
    privilegios: '',
    selecionar_todos: '',
    id: '',
    nome: '',
    data_de_inclusao: '',
    acoes: '',
    itens_por_pagina: ''
  };

  placeholders = {
    adicione_um_nome: '',
    buscar: ''
  };

  botoes = {
    adicionar: '',
    salvar_edicao: '',
    cancelar_edicao: '',
    novo_grupo: '',
    confirmar: '',
    cancelar: ''
  };

  tooltips = {
    editar: '',
    excluir: ''
  };

  // Menu kebab responsivo
  @ViewChild('menuAcoes') menuAcoes!: Menu;
  menuAcoesItems: MenuItem[] = [];

  private langChangeSub!: Subscription;

  // Tabela / paginacao
  gruposDePrivilegio: GrupoDePrivilegioDTO[] = [];
  totalRecords = 0;
  first = 0;
  rows = 10;
  private _lastRows: number = 10;
  loading = true;
  searchTerm = '';
  lastfield = 'id';
  lastSort = 'asc';
  private _skipSort = false;
  private search$ = new Subject<string>();

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'grupo-privilegio-visible-columns';

  columns: TableColumn[] = [
    { key: 'id', labelKey: 'grupo-de-privilegio.labels.id', visible: true, fixed: true, defaultVisible: true },
    { key: 'nome', labelKey: 'grupo-de-privilegio.labels.nome', visible: true, fixed: false, defaultVisible: true },
    { key: 'privilegios', labelKey: 'grupo-de-privilegio.labels.privilegios', visible: true, fixed: false, defaultVisible: true },
    { key: 'criadoEm', labelKey: 'grupo-de-privilegio.labels.data_de_inclusao', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'grupo-de-privilegio.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  constructor(
    private grupoDePrivilegioService: GrupoDePrivilegioService,
    private privilegioService: PrivilegioService,
    private messageService: MessageService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadColumnPreferences();
    this.loadPrivilegios();
    this.fetchGroups(0, this.rows);
    this.setupSearchDebounce();
    this.loadTranslations();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    this.search$.complete();
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  /** Configura debounce da busca server-side (300ms + distinctUntilChanged) */
  private setupSearchDebounce(): void {
    this.search$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.first = 0;
      this.fetchGroups(0, this.rows);
    });
  }

  // =========================================================================
  // Traducoes
  // =========================================================================

  onTabChange(event: { index: number }): void {
    if (event.index === 1) {
      this.texts.subtitulo = this.translate.instant('grupo-de-privilegio.texts.subtitulo_lista');
    } else {
      this.texts.subtitulo = this.translate.instant('grupo-de-privilegio.texts.subtitulo');
    }
  }

  private loadTranslations(): void {
    const isListaAtiva = this.activeTabIndex === 1;
    this.texts = {
      titulo: this.translate.instant('grupo-de-privilegio.texts.titulo'),
      subtitulo: this.translate.instant(
        isListaAtiva ? 'grupo-de-privilegio.texts.subtitulo_lista' : 'grupo-de-privilegio.texts.subtitulo'
      ),
      novo_grupo: this.translate.instant('grupo-de-privilegio.texts.novo_grupo'),
      editar_grupo: this.translate.instant('grupo-de-privilegio.texts.editar_grupo'),
      tabela_de_grupos: this.translate.instant('grupo-de-privilegio.texts.tabela_de_grupos'),
      nenhum_grupo: this.translate.instant('grupo-de-privilegio.texts.nenhum_grupo'),
      ajuste_filtros: this.translate.instant('grupo-de-privilegio.texts.ajuste_filtros'),
      carregando: this.translate.instant('grupo-de-privilegio.texts.carregando'),
      sem_privilegios: this.translate.instant('grupo-de-privilegio.texts.sem_privilegios'),
      confirmacao: this.translate.instant('common.confirmacao'),
      confirmar_exclusao: this.translate.instant('grupo-de-privilegio.mensagens.confirmar_exclusao'),
      aba_cadastro: this.translate.instant('grupo-de-privilegio.texts.aba_cadastro'),
      aba_lista: this.translate.instant('grupo-de-privilegio.texts.aba_lista')
    };

    this.labels = {
      nome_do_grupo: this.translate.instant('grupo-de-privilegio.labels.nome_do_grupo'),
      privilegios: this.translate.instant('grupo-de-privilegio.labels.privilegios'),
      selecionar_todos: this.translate.instant('grupo-de-privilegio.labels.selecionar_todos'),
      id: this.translate.instant('grupo-de-privilegio.labels.id'),
      nome: this.translate.instant('grupo-de-privilegio.labels.nome'),
      data_de_inclusao: this.translate.instant('grupo-de-privilegio.labels.data_de_inclusao'),
      acoes: this.translate.instant('grupo-de-privilegio.labels.acoes'),
      itens_por_pagina: this.translate.instant('grupo-de-privilegio.labels.itens_por_pagina')
    };

    this.placeholders = {
      adicione_um_nome: this.translate.instant('grupo-de-privilegio.placeholders.adicione_um_nome'),
      buscar: this.translate.instant('grupo-de-privilegio.placeholders.buscar')
    };

    this.botoes = {
      adicionar: this.translate.instant('grupo-de-privilegio.botoes.adicionar'),
      salvar_edicao: this.translate.instant('grupo-de-privilegio.botoes.salvar_edicao'),
      cancelar_edicao: this.translate.instant('grupo-de-privilegio.botoes.cancelar_edicao'),
      novo_grupo: this.translate.instant('grupo-de-privilegio.botoes.novo_grupo'),
      confirmar: this.translate.instant('grupo-de-privilegio.botoes.confirmar'),
      cancelar: this.translate.instant('grupo-de-privilegio.botoes.cancelar')
    };

    this.tooltips = {
      editar: this.translate.instant('grupo-de-privilegio.tooltips.editar'),
      excluir: this.translate.instant('grupo-de-privilegio.tooltips.excluir')
    };

    // Atualiza labelKeys das colunas
    this.columns = [
      { key: 'id', labelKey: 'grupo-de-privilegio.labels.id', visible: this.columns[0]?.visible ?? true, fixed: true, defaultVisible: true },
      { key: 'nome', labelKey: 'grupo-de-privilegio.labels.nome', visible: this.columns[1]?.visible ?? true, fixed: false, defaultVisible: true },
      { key: 'privilegios', labelKey: 'grupo-de-privilegio.labels.privilegios', visible: this.columns[2]?.visible ?? true, fixed: false, defaultVisible: true },
      { key: 'criadoEm', labelKey: 'grupo-de-privilegio.labels.data_de_inclusao', visible: this.columns[3]?.visible ?? true, fixed: false, defaultVisible: true },
      { key: 'acoes', labelKey: 'grupo-de-privilegio.labels.acoes', visible: this.columns[4]?.visible ?? true, fixed: true, defaultVisible: true },
    ];

    // Os títulos das seções também são traduzidos: remonta ao trocar de idioma.
    this.montarSecoes();
  }

  /** Reagrupa os privilégios por seção. Chamar sempre que a lista ou o idioma mudar. */
  private montarSecoes(): void {
    this.secoesPrivilegio = agruparPrivilegiosPorSecao(
      this.privilegiosList,
      (chave) => this.translate.instant(`grupo-de-privilegio.secoes.${chave}`)
    );
  }

  // =========================================================================
  // Carga dos privilegios (para o form)
  // =========================================================================

  loadPrivilegios(): void {
    // Busca todos os 20 valores do enum JanelaDeMenu diretamente,
    // sem depender dos registros que existem no banco de dados
    this.privilegioService.getJanelasDeMenu().subscribe({
      next: (janelas) => {
        this.privilegiosList = (janelas ?? []).map(j => ({
          id: j.id,
          codigoPrivilegio: 0,
          janelaDeMenu: j,
          consulta: true
        } as PrivilegioDTO));
        this.montarSecoes();
      },
      error: (err) => console.error('Erro ao carregar privilégios:', err),
    });
  }

  // =========================================================================
  // Tabela (lista & busca)
  // =========================================================================

  private fetchGroups(
    page: number,
    size: number,
    sort: string = `${this.lastfield},${this.lastSort}`,
    search: string | undefined = this.searchTerm?.trim() || undefined
  ): void {
    this.loading = true;

    // Quando há termo de busca, usa o endpoint server-side /grupoDePrivilegio/buscarPorNome.
    // Caso contrário, usa a listagem normal /grupoDePrivilegio.
    const endpoint = search ? '/grupoDePrivilegio/buscarPorNome' : '/grupoDePrivilegio';
    const params: any = search
      ? { nome: search, page, size, sort }
      : { page, size, sort };

    this.grupoDePrivilegioService.get(endpoint, params).subscribe({
      next: (res: GrupoDePrivilegioResponseDTO) => {
        this._skipSort = true;
        this.gruposDePrivilegio = (res.content || []).map(g => ({
          ...g,
          privilegios: g.privilegios || []
        }));
        this.totalRecords = res.totalElements ?? this.gruposDePrivilegio.length;
        this.first = page * size;
        this.loading = false;
      },
      error: (err) => {
        // O endpoint /grupoDePrivilegio/buscarPorNome retorna 404 quando não há resultados
        // (backend lança NotFoundException via validarListaNaoVazia).
        // Nesse caso, exibir lista vazia em vez de mensagem de erro.
        if (search && err?.status === 404) {
          this.gruposDePrivilegio = [];
          this.totalRecords = 0;
          this.first = 0;
        } else {
          console.error('Erro ao carregar grupos de privilégio:', err);
          this.gruposDePrivilegio = [];
          this.totalRecords = 0;
        }
        this.loading = false;
      }
    });
  }

  onPageChange(event: any): void {
    const page = event.page ?? Math.floor(event.first / event.rows);
    this.first = event.first;
    this.rows = event.rows;
    this.fetchGroups(page, this.rows, `${this.lastfield},${this.lastSort}`);
  }

  onRowsPerPageChange(): void {
    if (this.rows === this._lastRows) return;
    this._lastRows = this.rows;
    this.first = 0;
    this.fetchGroups(0, this.rows, `${this.lastfield},${this.lastSort}`);
  }

  onSearchChange(value: string): void {
    this.searchTerm = value || '';
    this.search$.next(this.searchTerm);
  }

  // =========================================================================
  // Ordenacao server-side
  // =========================================================================

  onSort(event: { field: string; order: number }): void {
    if (this._skipSort) {
      this._skipSort = false;
      return;
    }
    if (!event.field) return;

    this.lastfield = event.field;
    this.lastSort = event.order === 1 ? 'asc' : 'desc';

    const page = Math.floor(this.first / this.rows);
    this.fetchGroups(page, this.rows, `${this.lastfield},${this.lastSort}`);
  }

  formatarDataEHora(dataISO: string): string {
    if (!dataISO) return '';
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) return '';
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  // =========================================================================
  // Salvar
  // =========================================================================

  saveGrupoDePrivilegio(): void {
    if (!this.form.nome) {
      this.messageService.add({ severity: 'error', summary: this.translate.instant('common.erro'), detail: this.translate.instant('grupo-de-privilegio.mensagens.campo_nome_obrigatorio') });
      return;
    }
    if (!this.selectedPrivilegios.length) {
      this.messageService.add({ severity: 'warn', summary: this.translate.instant('common.atencao'), detail: this.translate.instant('grupo-de-privilegio.mensagens.selecione_privilegio') });
      return;
    }

    if (this.editandoId !== null) {
      this.updateGrupoDePrivilegio();
      return;
    }

    const body: GrupoDePrivilegioDTO = {
      nome: this.form.nome!,
      status: true,
      privilegios: this.selectedPrivilegios.map(enumValue => ({
        janelaDeMenu: enumValue,
        consulta: true
      })) as any
    };

    this.grupoDePrivilegioService.post('/grupoDePrivilegio', body).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: this.translate.instant('common.sucesso'), detail: this.translate.instant('grupo-de-privilegio.mensagens.grupo_criado') });
        this.resetForm();
        this.fetchGroups(0, this.rows);
        this.activeTabIndex = 1;
      },
      error: () => {},
    });
  }

  private updateGrupoDePrivilegio(): void {
    const body: GrupoDePrivilegioDTO = {
      id: this.editandoId!,
      nome: this.form.nome!,
      status: this.form.status ?? true,
      privilegios: this.selectedPrivilegios.map(enumValue => ({
        janelaDeMenu: enumValue,
        consulta: true
      })) as any
    };

    this.grupoDePrivilegioService.put(`/grupoDePrivilegio/${this.editandoId}`, body).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: this.translate.instant('common.sucesso'), detail: this.translate.instant('grupo-de-privilegio.mensagens.grupo_editado') });
        this.resetForm();
        this.fetchGroups(0, this.rows);
        this.activeTabIndex = 1;
      },
      error: () => {},
    });
  }

  // =========================================================================
  // Exclusao
  // =========================================================================

  deletarGrupo(id: string) {
    this.deleteItemId = id;
    this.deleteDialogVisible = true;
  }

  confirmarExclusao() {
    if (!this.deleteItemId) return;
    this.grupoDePrivilegioService.delete(`/grupoDePrivilegio/${this.deleteItemId}`).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: this.translate.instant('common.sucesso'), detail: this.translate.instant('grupo-de-privilegio.mensagens.apagado_sucesso') });
        this.fetchGroups(0, this.rows);
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
      error: () => {
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
    });
  }

  // =========================================================================
  // Auxiliares do formulario
  // =========================================================================

  toggleAllPrivileges(selected: boolean): void {
    this.allSelected = selected;
    this.selectedPrivilegios = selected
      ? (this.privilegiosList || []).map(p => p.janelaDeMenu?.enum).filter(Boolean) as string[]
      : [];
  }

  resetForm(): void {
    this.form = { nome: '', status: true, privilegios: [] };
    this.selectedPrivilegios = [];
    this.allSelected = false;
    this.editandoId = null;
  }

  editarGrupo(id: number): void {
    this.grupoDePrivilegioService.getById(`/grupoDePrivilegio/${id}`).subscribe({
      next: (grupo: GrupoDePrivilegioDTO) => {
        this.editandoId = grupo.id ?? id;
        this.form = { nome: grupo.nome, status: grupo.status };

        // Extrair os ENUMs dos privilegios do grupo para marcar os checkboxes
        this.selectedPrivilegios = (grupo.privilegios ?? [])
          .map(p => {
            const jm = p.janelaDeMenu as any;
            return jm?.enum ?? jm;
          })
          .filter(Boolean) as string[];

        this.allSelected = this.selectedPrivilegios.length === this.privilegiosList.length;
        this.activeTabIndex = 0;
        this.cdr.detectChanges();
        this.scrollToFormulario();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('grupo-de-privilegio.mensagens.erro_carregar')
        });
      }
    });
  }

  novoGrupo(): void {
    this.resetForm();
    this.activeTabIndex = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // =========================================================================
  // Menu kebab responsivo (acoes da tabela em telas <= 1000px)
  // =========================================================================

  toggleMenuAcoes(event: Event, grupo: GrupoDePrivilegioDTO): void {
    this.menuAcoesItems = this.buildMenuAcoesItems(grupo);
    this.menuAcoes.toggle(event);
  }

  private buildMenuAcoesItems(grupo: GrupoDePrivilegioDTO): MenuItem[] {
    return [
      {
        label: this.tooltips.editar,
        icon: 'pi pi-pencil',
        command: () => this.editarGrupo(grupo.id!)
      },
      {
        label: this.tooltips.excluir,
        icon: 'pi pi-trash',
        command: () => this.deletarGrupo(String(grupo.id))
      }
    ];
  }

  scrollToFormulario(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // =========================================================================
  // Colunas visiveis
  // =========================================================================

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferências de colunas:', e);
    }
  }
}
